import { Despesa, BalanceItem } from '@/types/app';
import { logger } from './logger';

// Novo tipo auxiliar para mapear id de bolso ou participante para nome
interface BolsoOuParticipante {
  id: string;
  nome: string;
}

// Função para consolidar saldos por vínculo de bolso
export function consolidateSaldosPorBolso(
  saldosIndividuais: { [key: string]: number },
  participantes: Array<{ id: string; nome: string; grupoDeBolsoId?: string | null }>,
  gruposDeBolso: Array<{ id: string; nome: string }>
): { [key: string]: { saldo: number; nome: string } } {
  if (!Array.isArray(participantes)) {
    logger.error('[consolidateSaldosPorBolso] participantes is not array', { participantes });
    return {};
  }
  if (!Array.isArray(gruposDeBolso)) {
    logger.error('[consolidateSaldosPorBolso] gruposDeBolso is not array', { gruposDeBolso });
    return {};
  }
  const saldosPorBolso: { [key: string]: { saldo: number; nome: string } } = {};

  // Mapear vínculos de bolso
  gruposDeBolso.forEach(grupo => {
    saldosPorBolso[grupo.id] = { saldo: 0, nome: grupo.nome };
  });

  // Participantes sem vínculo de bolso ficam com seu próprio id
  participantes.forEach(p => {
    const saldo = saldosIndividuais[p.id] || 0;
    if (p.grupoDeBolsoId && saldosPorBolso[p.grupoDeBolsoId]) {
      saldosPorBolso[p.grupoDeBolsoId].saldo += saldo;
    } else {
      saldosPorBolso[p.id] = { saldo, nome: p.nome };
    }
  });

  // NÃO remover grupos com saldo zero - eles podem ter participado de outras despesas
  // Apenas remover se o saldo for exatamente zero E não for um grupo de bolso definido
  Object.keys(saldosPorBolso).forEach(id => {
    const isGrupoDeBolso = gruposDeBolso.some(grupo => grupo.id === id);
    if (Math.abs(saldosPorBolso[id].saldo) < 0.01 && !isGrupoDeBolso) {
      // Só remove se não for um grupo de bolso oficial
      delete saldosPorBolso[id];
    }
  });

  return saldosPorBolso;
}

// Refatorar calculateBalance para aceitar vínculos de bolso
export function calculateBalance(
  despesas: Despesa[],
  participantes: Array<{ id: string; nome: string; grupoDeBolsoId?: string | null }>,
  gruposDeBolso: Array<{ id: string; nome: string }>
): BalanceItem[] {
  const saldos: { [key: string]: number } = {};
  participantes.forEach(p => {
    saldos[p.id] = 0;
  });

  // Calcular saldos baseado nas despesas - VERSÃO CORRIGIDA
  despesas.forEach((despesa) => {
    const pagadorId = String(despesa.pagoPorId);
    saldos[pagadorId] = (saldos[pagadorId] || 0) + despesa.valor;
    despesa.divisoes.forEach(divisao => {
      const participanteId = String(divisao.usuarioId);
      saldos[participanteId] = (saldos[participanteId] || 0) - divisao.valorDevido;
    });
  });

  // Consolidar por bolso
  const saldosConsolidados = consolidateSaldosPorBolso(saldos, participantes, gruposDeBolso);

  // Algoritmo guloso de quitação de dívidas entre bolsos
  const transacoes: BalanceItem[] = [];
  let devedores = Object.entries(saldosConsolidados)
    .filter(([_, v]) => v.saldo < -0.01)
    .map(([id, v]) => ({ id, saldo: v.saldo, nome: v.nome }));
  let credores = Object.entries(saldosConsolidados)
    .filter(([_, v]) => v.saldo > 0.01)
    .map(([id, v]) => ({ id, saldo: v.saldo, nome: v.nome }));
  devedores.sort((a, b) => a.saldo - b.saldo);
  credores.sort((a, b) => b.saldo - a.saldo);
  while (devedores.length > 0 && credores.length > 0) {
    // Verificar se os arrays não estão vazios antes de acessar o índice [0]
    if (devedores.length === 0 || credores.length === 0) {
      break;
    }
    
    const devedor = devedores[0];
    const credor = credores[0];
    
    // Verificar se devedor e credor são válidos
    if (!devedor || !credor) {
      break;
    }
    const valorTransferencia = Math.min(Math.abs(devedor.saldo), credor.saldo);
    transacoes.push({
      devedorId: devedor.id,
      credorId: credor.id,
      valor: valorTransferencia,
      devedorNome: devedor.nome,
      credorNome: credor.nome,
    });
    devedor.saldo += valorTransferencia;
    credor.saldo -= valorTransferencia;
    if (Math.abs(devedor.saldo) < 0.01) devedores.shift();
    if (credor.saldo < 0.01) credores.shift();
    devedores.sort((a, b) => a.saldo - b.saldo);
    credores.sort((a, b) => b.saldo - a.saldo);
  }
  
  return transacoes;
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}